import { App } from '@capacitor/app';
import { Capacitor } from '@capacitor/core';
import { RefObject, useEffect, useRef } from 'react';
import type { MapRef } from 'react-map-gl';

import { resumeMapboxMap } from '../resume-mapbox-map/resume-mapbox-map';

export type UseResumeMapboxMapOptions = {
  enabled?: boolean;
  onWebGlContextLost?: () => void;
};

export function useResumeMapboxMap(
  mapRef: RefObject<MapRef | null>,
  mapLoaded: boolean,
  options?: UseResumeMapboxMapOptions
): void {
  const enabled = options?.enabled ?? true;
  const onWebGlContextLostRef = useRef(options?.onWebGlContextLost);
  onWebGlContextLostRef.current = options?.onWebGlContextLost;

  useEffect(() => {
    if (!enabled || !mapLoaded) {
      return undefined;
    }

    const map = mapRef.current?.getMap();
    if (!map) {
      return undefined;
    }

    const canvas = map.getCanvas();
    const handleContextLost = (event: Event) => {
      event.preventDefault();
      onWebGlContextLostRef.current?.();
    };
    canvas.addEventListener('webglcontextlost', handleContextLost);

    const resume = () => {
      requestAnimationFrame(() => {
        resumeMapboxMap(mapRef.current?.getMap());
      });
    };

    let removeAppListener: (() => void) | undefined;
    if (Capacitor.isNativePlatform()) {
      void App.addListener('appStateChange', ({ isActive }) => {
        if (isActive) {
          resume();
        }
      }).then((listener) => {
        removeAppListener = () => listener.remove();
      });
    }

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        resume();
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      canvas.removeEventListener('webglcontextlost', handleContextLost);
      removeAppListener?.();
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [enabled, mapLoaded, mapRef]);
}
